
import { AfterViewInit, ChangeDetectionStrategy, Component, contentChild, inject, Signal, DestroyRef } from '@angular/core';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { MatChipsModule } from '@angular/material/chips';
import { IconModule } from '@ogs-gmbh/ngx-m3-components';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, tap, withLatestFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { MatRippleModule } from '@angular/material/core';
import { TranslationInfo } from '@shared/types/translation-info';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableConfigService } from '@core/services/table/table-config.service';
import { TableConfig, TableOptions } from '../table-implementation/types/table-config';
import { OverviewHeaderService } from './services/overview-header.service';
import { NotifyChipComponent } from "../notify-chip/notify-chip.component";
import { NotifyChipValueComponent } from "../notify-chip/components/notify-chip-value/notify-chip-value.components";
import { OverviewSettingsMenuService } from './services/overview-settings-menu.service';
import { StatusType } from '@shared/types/status.types';
import { OverviewHeaderFilterChipsToken } from './components/overview-header-filter-chips/overview-header-filter-chips.component';
import { OverviewHeaderSearchComponent } from './components/overview-header-search/overview-header-search.component';
import { OverviewHeaderSettingsComponent } from './components/overview-header-operators/components/overview-header-settings/overview-header-settings.component';
import { OverviewSettingsMenuComponent } from "./components/overview-settings-menu/overview-settings-menu.component";

@Component({
  selector: 'app-overview-header',
  standalone: true,
  imports: [ MatChipsModule,
    MatRippleModule,
    IconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslationPipeModule,
    NotifyChipComponent,
    NotifyChipValueComponent,
    OverviewSettingsMenuComponent ],
  templateUrl: './overview-header.component.html',
  styleUrl: './overview-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    OverviewSettingsMenuService,
    OverviewHeaderService
  ]
})
export class OverviewHeaderComponent implements AfterViewInit {
  private readonly _tableConfigService: TableConfigService | null = inject(TableConfigService, { optional: true });

  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly filterChipsComponent: Signal<OverviewHeaderFilterChipsToken | undefined> = contentChild(OverviewHeaderFilterChipsToken);

  protected readonly settingsComponent: Signal<OverviewHeaderSettingsComponent | undefined> = contentChild(OverviewHeaderSettingsComponent);

  protected searchComponent: Signal<OverviewHeaderSearchComponent | undefined> = contentChild(OverviewHeaderSearchComponent);

  protected overviewHeaderService: OverviewHeaderService = inject(OverviewHeaderService);

  private _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  public tableSearch: FormControl = new FormControl();

  constructor () {
    this._faIconLibrary.addIcons(faSearch);
  }

  public ngAfterViewInit (): void {
    if (!this._tableConfigService) return;

    this.tableSearch.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this._destroyRef),
      distinctUntilChanged(),
      // eslint-disable-next-line @tseslint/no-unsafe-argument
      withLatestFrom(this._tableConfigService.selectedConfig$),
      tap(([ searchTerm, config ]: [string, TableConfig]) => {
        const options: TableOptions = config.options
          ? { ...config.options, searchTerm }
          : { searchTerm };

        // eslint-disable-next-line @tseslint/no-non-null-assertion
        this._tableConfigService!.patchTableOptionsLocally(options);
      })
    ).subscribe();
  }


  public showToast (statusType: StatusType, localizedText: TranslationInfo): void {
    this.overviewHeaderService.showToast(statusType, localizedText);
  }
}
